import { useState, useMemo } from "react";
import {
  useBookings,
  useCreateBooking,
  useCancelBooking,
  useUpdateBooking,
} from "@/hooks/useBookings";
import { useServicesByPractice } from "@/hooks/useServices";
import { useAllPractices } from "@/hooks/usePractices";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, Building2 } from "lucide-react";
import { isSameDay } from "date-fns";
import { transformServiceToFrontend } from "@/services/transformers";
import { showSuccess, showError } from "@/utils/toastHelpers";

import ServiceCalendar from "../components/services/ServiceCalendar";
import PatientAppointmentForm from "../components/services/PatientAppointmentForm";
import DailySchedule from "../components/services/DailySchedule";
import ServiceSelectionModal from "../components/services/ServiceSelectionModal";

export default function PatientPortal() {
  // React Query hooks for data fetching
  const { data: appointments = [] } = useBookings();
  const { data: allPractices = [] } = useAllPractices();
  
  // Get all services for appointment viewing
  const { data: allServices = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => apiClient.get("/api/services"),
  });

  // UI State
  const [selectedPractice, setSelectedPractice] = useState("");

  // Get services for selected practice
  const { data: services = [] } = useServicesByPractice(selectedPractice);

  // Mutations
  const createBookingMutation = useCreateBooking();
  const cancelBookingMutation = useCancelBooking();
  const updateBookingMutation = useUpdateBooking();

  // UI State
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [serviceForBooking, setServiceForBooking] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointmentToEdit, setAppointmentToEdit] = useState(null);

  const handleScheduleNewAppointment = () => {
    setServiceForBooking(null);
    setShowServiceSelection(true);
  };

  const handleServiceSelectedForBooking = (serviceWithPractice) => {
    setServiceForBooking(serviceWithPractice);
    setShowServiceSelection(false);
    setShowAppointmentForm(true);
  };

  const handleSaveAppointment = async (data) => {
    try {
      if (appointmentToEdit) {
        // Update existing appointment
        await updateBookingMutation.mutateAsync({ id: appointmentToEdit.id, ...data });
      } else {
        // Create new appointment
        await createBookingMutation.mutateAsync(data);
      }
      setShowAppointmentForm(false);
      setServiceForBooking(null);
      setAppointmentToEdit(null);
      showSuccess("Appointment saved successfully");
    } catch (error) {
      console.error("Error saving appointment:", error);
      showError("Failed to save appointment", error.message);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) {
      return;
    }

    try {
      await cancelBookingMutation.mutateAsync(appointmentId);
      showSuccess("Appointment cancelled successfully");
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      showError("Failed to cancel appointment", error.message);
    }
  };

  const handleViewAppointment = (appointment) => {
    // Find the service for this appointment
    const service = allServices.find(s => s.id === appointment.service_id);
    setAppointmentToEdit(appointment);
    // Transform service to frontend naming convention (snake_case)
    setServiceForBooking(service ? transformServiceToFrontend(service) : null);
    setShowAppointmentForm(true);
  };

  // Filter appointments for selected date - patients only see their own appointments
  const appointmentsForSelectedDay = useMemo(() => {
    if (!Array.isArray(appointments) || !selectedDate) {
      return [];
    }

    return appointments
      .filter((apt) => {
        try {
          if (!apt || !apt.appointment_date) return false;

          // Filter out cancelled appointments
          if (apt.status === "cancelled") return false;

          const appointmentDate = new Date(apt.appointment_date);
          const dateMatch = isSameDay(appointmentDate, selectedDate);
          const practiceMatch =
            !selectedPractice || apt.practice_id === selectedPractice;
          return dateMatch && practiceMatch;
        } catch (error) {
          console.error("Error comparing dates:", error);
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return new Date(a.appointment_date) - new Date(b.appointment_date);
        } catch (error) {
          console.error("Error sorting appointments:", error);
          return 0;
        }
      });
  }, [appointments, selectedDate, selectedPractice]);

  // Filter appointments for the calendar view (all dates) - patients only see their own
  const filteredAppointments = useMemo(() => {
    return appointments.filter((apt) => {
      // Filter out cancelled appointments
      if (apt.status === "cancelled") return false;

      const practiceMatch =
        !selectedPractice || apt.practice_id === selectedPractice;
      return practiceMatch;
    });
  }, [appointments, selectedPractice]);

  const clearAllFilters = () => {
    setSelectedPractice("");
  };

  const hasActiveFilters = !!selectedPractice;

  return (
    <div className="p-6 max-w-full mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Book Your Appointment
          </h1>
          <p className="text-slate-600">
            Select a service and schedule your appointment.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleScheduleNewAppointment}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            Schedule Appointment
          </Button>
        </div>
      </div>

      {/* Practice Selection */}
      <div className="bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-lg p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">
              Select Practice:
            </span>
          </div>
          <div className="flex flex-wrap gap-4">
            <Select
              value={selectedPractice}
              onValueChange={setSelectedPractice}
            >
              <SelectTrigger className="w-64 bg-white border-slate-300">
                <SelectValue placeholder={
                  allPractices.length === 0 
                    ? "No practices available..." 
                    : "Choose a practice to view services..."
                } />
              </SelectTrigger>
              <SelectContent>
                {allPractices.length === 0 ? (
                  <SelectItem value="no-practices" disabled>
                    No active practices with services available
                  </SelectItem>
                ) : (
                  allPractices.map((practice) => (
                    <SelectItem
                      key={practice.practiceId}
                      value={practice.practiceId}
                    >
                      {practice.practiceName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                className="text-slate-600 hover:text-slate-800"
              >
                Clear Selection
              </Button>
            )}
          </div>
        </div>
        {selectedPractice && (
          <div className="mt-4 text-sm text-slate-600">
            Showing services and appointments for{" "}
            <span className="font-medium">
              {
                allPractices.find((p) => p.practiceId === selectedPractice)
                  ?.practiceName
              }
            </span>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <ServiceCalendar
            appointments={filteredAppointments}
            unavailability={[]}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
        </div>

        <div>
          <DailySchedule
            date={selectedDate}
            appointments={appointmentsForSelectedDay}
            unavailability={[]}
            services={services}
            onComplete={() => {}}
            onDeleteAppointment={handleDeleteAppointment}
            onDeleteUnavailability={() => {}}
            onEditUnavailability={() => {}}
            onViewAppointment={handleViewAppointment}
            isPatientView={true}
          />
        </div>
      </div>

      {/* Forms and Modals */}
      {showServiceSelection && (
        <ServiceSelectionModal
          allPractices={allPractices}
          onSelect={handleServiceSelectedForBooking}
          onClose={() => setShowServiceSelection(false)}
        />
      )}

      {showAppointmentForm && (
        <PatientAppointmentForm
          appointmentToEdit={appointmentToEdit}
          serviceForBooking={serviceForBooking}
          services={serviceForBooking ? [serviceForBooking] : []}
          onSave={handleSaveAppointment}
          onClose={() => {
            setShowAppointmentForm(false);
            setServiceForBooking(null);
            setAppointmentToEdit(null);
          }}
        />
      )}
    </div>
  );
}
